import React from "react";
import { DimensionContext } from "@/types/data";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { Person } from "@/features/people/graphql/generated";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { useCompetencyInsights } from "@/shared/hooks/insights/useCompetencyInsights";
import { InsightsSection } from ".";
import { includes } from "lodash";
import { useTranslation } from "react-i18next";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { Column } from "@/shared/ui";

interface CompetencyInsightsSectionProps {
  dimensions?: DimensionWithScores[];
  context?: DimensionContext;
  person?: Person;
  scorecardRows?: ScorecardRow[];
}

export const CompetencyInsights = (props: CompetencyInsightsSectionProps) => {
  const { dimensions, person, scorecardRows, context = "person" } = props;

  const { t } = useTranslation();

  // Map DimensionContext to the hook's expected context type
  const hookContext =
    context === "user_in_team" ? "person" : (context as DimensionContext);

  const insights = useCompetencyInsights({
    dimensions: dimensions || [],
    context: hookContext,
    person,
    scorecardRows,
  });

  const getTopsLabel = () => {
    if (context === "team") return t("competency_insights.team_strengths");

    return t("competency_insights.strengths");
  };

  const getBottomsLabel = () => {
    if (context === "team") return t("competency_insights.potential_gaps");

    return t("competency_insights.development_areas");
  };

  const filterButtons = [
    { key: "all", label: t("competency_insights.all_insights") },
    { key: "likely_strengths", label: getTopsLabel() },
    { key: "development_potential", label: getBottomsLabel() },
    {
      key: "unique_skills",
      label: t("competency_insights.unique_skills"),
      hidden: includes(["candidate", "user", "person"], context),
    },
  ].filter((button) => !button.hidden);

  return (
    <Column className="mt-16">
      <SectionHeader
        title={t("profile.competency_insights_title")}
        subtitle={t("profile.competency_insights_description", {
          context: t(`context_label.${context}_posessive`),
        })}
        className="mb-0"
      />
      <InsightsSection
        insights={insights}
        filters={filterButtons}
        context={context}
      />
    </Column>
  );
};
